import type { ReactNode } from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/context/AuthContext'
import type { Report, Role } from '@/types'

const EDIT_WINDOW_MS = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

interface EditWindowStatus {
  /** Whether the user can edit this report */
  canEdit: boolean
  /** Reason why editing is not allowed (if canEdit is false) */
  reason?: string
  /** Time remaining in the edit window (in ms), or undefined if expired/not applicable */
  timeRemaining?: number
  /** Formatted time remaining as human-readable string */
  timeRemainingFormatted?: string
  /** Whether the edit window is still counting down */
  isCountingDown: boolean
}

/**
 * Formats milliseconds into a human-readable countdown string.
 */
function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return 'Expired'

  const hours = Math.floor(ms / (60 * 60 * 1000))
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000))

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`
  }
  return `${minutes}m remaining`
}

/**
 * Determines if a user can edit a report based on role and time window.
 *
 * Rules:
 * - super_admin and admin can always edit
 * - employee can only edit their own reports within 24 hours of creation
 * - viewer cannot edit
 */
function canUserEditReport(
  userId: string | undefined,
  userRole: Role | undefined,
  report: Report
): { canEdit: boolean; reason?: string } {
  if (!userId || !userRole) {
    return { canEdit: false, reason: 'Not authenticated' }
  }

  // Super admin and admin can always edit
  if (userRole === 'super_admin' || userRole === 'admin') {
    return { canEdit: true }
  }

  // Viewer cannot edit
  if (userRole === 'viewer') {
    return { canEdit: false, reason: 'Viewers cannot edit reports' }
  }

  // Employee - check ownership and time window
  if (userRole === 'employee') {
    if (report.employee_id !== userId) {
      return { canEdit: false, reason: 'You can only edit your own reports' }
    }

    const createdAt = new Date(report.created_at).getTime()
    const now = Date.now()
    const elapsed = now - createdAt

    if (elapsed >= EDIT_WINDOW_MS) {
      return {
        canEdit: false,
        reason: 'Edit window has closed (24 hours from creation)',
      }
    }

    return { canEdit: true }
  }

  return { canEdit: false, reason: 'Unknown role' }
}

/**
 * Hook to track the edit window status for a report.
 * Updates every minute while the window is active.
 */
export function useEditWindow(report: Report | undefined): EditWindowStatus {
  const { user } = useAuth()
  const [now, setNow] = useState(Date.now())

  // Update time every minute while counting down
  useEffect(() => {
    if (!report) return

    const createdAt = new Date(report.created_at).getTime()
    const windowEnd = createdAt + EDIT_WINDOW_MS

    // Only set interval if window hasn't closed and user is employee
    if (user?.role === 'employee' && windowEnd > now) {
      const interval = setInterval(() => {
        setNow(Date.now())
      }, 60 * 1000) // Update every minute

      return () => clearInterval(interval)
    }
  }, [report, user?.role, now])

  // Recalculate status
  const getStatus = useCallback((): EditWindowStatus => {
    if (!report) {
      return { canEdit: false, isCountingDown: false }
    }

    const { canEdit, reason } = canUserEditReport(user?.id, user?.role, report)

    // For employees with edit permission, calculate remaining time
    if (user?.role === 'employee' && canEdit) {
      const createdAt = new Date(report.created_at).getTime()
      const windowEnd = createdAt + EDIT_WINDOW_MS
      const timeRemaining = Math.max(0, windowEnd - now)

      return {
        canEdit: timeRemaining > 0,
        reason: timeRemaining <= 0 ? 'Edit window has closed' : undefined,
        timeRemaining,
        timeRemainingFormatted: formatTimeRemaining(timeRemaining),
        isCountingDown: timeRemaining > 0,
      }
    }

    return {
      canEdit,
      reason,
      isCountingDown: false,
    }
  }, [report, user?.id, user?.role, now])

  return getStatus()
}

/**
 * Component to display the edit window countdown timer.
 */
export function EditWindowBadge({
  status,
}: {
  status: EditWindowStatus
}): ReactNode {
  if (!status.isCountingDown) {
    return null
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
      {status.timeRemainingFormatted}
    </span>
  )
}
